import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import s from '../Inspection.css';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

// Fullscreen viewer with a simple zoom (R14: departure and damage photos can be compared closely).
export const PhotoViewer = withStyles(s)(injectIntl(({ photos, index, onClose, onIndex, intl }) => {
  const { formatMessage } = intl;
  const [zoom, setZoom] = useState(false);
  const photo = photos[index];
  if (!photo) return null;
  const move = (step) => { setZoom(false); onIndex((index + step + photos.length) % photos.length); };
  return (
    <div className={s.viewerBackdrop} role="dialog" aria-modal="true" aria-label={formatMessage(messages.inspViewerTitle)}
      onKeyDown={(event) => { if (event.key === 'Escape') onClose(); }}>
      <div className={s.viewerScroll}>
        <img className={cx(s.viewerImage, zoom && s.zoomed)} src={photo.url || photo.thumbUrl}
          alt={formatMessage(messages.inspPhotoAlt, { index: index + 1, total: photos.length })} />
      </div>
      <div className={s.viewerBar}>
        <button type="button" className={s.viewerButton} onClick={() => move(-1)} aria-label={formatMessage(messages.inspPrevious)}>{'‹'}</button>
        <span>{formatMessage(messages.inspPhotoAlt, { index: index + 1, total: photos.length })}</span>
        <button type="button" className={s.viewerButton} onClick={() => move(1)} aria-label={formatMessage(messages.inspNext)}>{'›'}</button>
        <button type="button" className={s.viewerButton} onClick={() => setZoom(!zoom)} aria-pressed={zoom}>{formatMessage(zoom ? messages.inspZoomOut : messages.inspZoomIn)}</button>
        <button type="button" className={s.viewerButton} onClick={onClose} autoFocus>{formatMessage(messages.inspClose)}</button>
      </div>
    </div>
  );
}));

// Grid of the photos of one series (4 x 2 for 8 photos). `items` are the photos stored by the server plus the local
// uploads in progress: { id?, localId?, thumbUrl?, url?, status: 'ready' | 'uploading' | 'failed', errorKey? }.
// editable: shows the add tile and the delete buttons. onAdd(files[]) / onRemove(item) / onRetry(item) are provided by the page.
export function InspectionPhotoGrid({ items, max, editable, onAdd, onRemove, onRetry, onViewAll, intl, title, hideCounter }) {
  const { formatMessage } = intl;
  const inputRef = useRef(null);
  const [viewer, setViewer] = useState(-1);
  const list = items || [];
  const ready = list.filter((item) => item.status === 'ready' || !item.status);
  const used = list.filter((item) => item.status !== 'failed').length;
  const full = used >= max;
  const total = ready.length;

  const pick = (event) => {
    const files = Array.from(event.target.files || []).slice(0, Math.max(0, max - used));
    event.target.value = '';
    if (files.length && onAdd) onAdd(files);
  };

  return (
    <div>
      <ul className={s.photoGrid} aria-label={title}>
        {list.map((item, index) => {
          const readyIndex = ready.indexOf(item);
          const key = item.id !== undefined && item.id !== null ? `p${item.id}` : `l${item.localId}`;
          return (
            <li key={key} className={s.photoItem}>
              {item.status === 'uploading' && <div className={s.photoLoading} role="status">{formatMessage(messages.inspUploading)}</div>}
              {item.status === 'failed' && (
                <div className={s.photoFailed} role="alert">
                  <span>{formatMessage(messages[item.errorKey || 'inspUploadFailed'])}</span>
                  {onRetry && item.file && <button type="button" className={s.photoRetry} onClick={() => onRetry(item)}>{formatMessage(messages.inspRetry)}</button>}
                </div>
              )}
              {(item.status === 'ready' || !item.status) && (
                <button type="button" className={s.photoButton} onClick={() => setViewer(readyIndex)} aria-label={formatMessage(messages.inspPhotoOpen, { index: readyIndex + 1, total })}>
                  <img className={s.photoImage} src={item.thumbUrl || item.url} loading="lazy" alt={formatMessage(messages.inspPhotoAlt, { index: readyIndex + 1, total })} />
                </button>
              )}
              {editable && item.status !== 'uploading' && (
                <button type="button" className={s.photoRemove} onClick={() => onRemove && onRemove(item)} aria-label={formatMessage(messages.inspPhotoRemove, { index: index + 1 })}>{'×'}</button>
              )}
            </li>
          );
        })}
        {editable && (
          <li>
            <label className={cx(s.addPhoto, full && s.addPhotoDisabled)}>
              <span aria-hidden="true">+</span>
              <span>{formatMessage(messages.inspAddPhoto)}</span>
              <small>{formatMessage(messages.inspPhotoMaxHint, { max })}</small>
              <input ref={inputRef} type="file" accept={ACCEPT} multiple className={s.hiddenInput} disabled={full} onChange={pick}
                aria-label={formatMessage(messages.inspAddPhoto)} />
            </label>
          </li>
        )}
        {!editable && onViewAll && total > 0 && (
          <li>
            <button type="button" className={s.viewAll} onClick={onViewAll}>{formatMessage(messages.inspViewAll)}</button>
          </li>
        )}
      </ul>
      {!hideCounter && <p className={s.photoCounter}>{formatMessage(messages.inspPhotoCount, { count: used, max })}</p>}
      {viewer >= 0 && <PhotoViewer photos={ready} index={viewer} onIndex={setViewer} onClose={() => setViewer(-1)} />}
    </div>
  );
}

InspectionPhotoGrid.propTypes = {
  items: PropTypes.array,
  max: PropTypes.number,
  editable: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onRetry: PropTypes.func,
  onViewAll: PropTypes.func,
  title: PropTypes.string,
  hideCounter: PropTypes.bool
};
InspectionPhotoGrid.defaultProps = { items: [], max: 8, editable: false };

export default injectIntl(withStyles(s)(InspectionPhotoGrid));
